import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react';

type AvailableValueType = string | number | boolean | null | undefined;
type AvailableValueOrArrayOfValueType = AvailableValueType | Array<AvailableValueType>;
type AvailableType<T> = {[K in keyof T]?: T[K] extends AvailableValueOrArrayOfValueType ? AvailableValueOrArrayOfValueType : never};
type AvailableObjectType = Record<string, AvailableValueType | Array<AvailableValueType>>;

const DESERIALIZE_REGEX = /^([^.~!_-]+)([.~!_-])(.*)$/;
const DESERIALIZE_ARRAY_REGEX = /[~!_-][^~!_-]+/g;

const urlStore = (function UrlStore() {
  let eventListeners: Array<Function> = [];
  const data = new Map<string, AvailableObjectType>();

  function syncData(key: string, defaultValue: AvailableObjectType) {
    const queryString = new URLSearchParams(window.location.search);
    let value = {...defaultValue};
    if (queryString.get(key)) {
      value = {...defaultValue, ...urlSafesDeserialize(queryString.get(key)!)};
    }
    data.set(key, value);
    emitEvent();
  }

  function appendData(
    key: string,
    setter: ((value: AvailableObjectType) => AvailableObjectType) | AvailableObjectType,
    isReplace: boolean
  ) {
    let newValue;
    if (typeof setter === 'function') {
      newValue = {...setter(data.get(key)!)};
    } else {
      newValue = {...data.get(key)!, ...setter};
    }
    data.set(key, newValue);
    appendRequest(key, urlSafesSerialize(newValue), isReplace);
  }

  function subscribe(listener: Function) {
    eventListeners.push(listener);
    return () => {
      eventListeners = eventListeners.filter((item) => item !== listener);
    };
  }

  function getSnapshot(key: string, defaultValue: AvailableObjectType) {
    if (!data.get(key)) {
      syncData(key, defaultValue);
    }
    return data.get(key);
  }

  function emitEvent() {
    eventListeners.forEach((listener) => listener());
  }

  let requestQueue: Array<{key: string; serialized: string; isReplace: boolean}> = [];

  function appendRequest(key: string, serialized: string, isReplace: boolean) {
    requestQueue.push({key, serialized, isReplace});
    consumeRequestQueue();
  }

  let consumeId: null | ReturnType<typeof setTimeout> = null;

  function consumeRequestQueue() {
    if (requestQueue.length === 0) {
      return;
    }

    if (consumeId) {
      clearTimeout(consumeId);
    }

    consumeId = setTimeout(() => {
      const requests = [...requestQueue];
      requestQueue = [];

      const isReplace = requests.every((request) => request.isReplace);
      const url = requests.reduce((url, request) => {
        url.searchParams.set(request.key, request.serialized);
        return url;
      }, new URL(window.location.href));

      // react-router 의 location.search 변경을 트리거하지 않기 위해 history api 직접 사용
      if (isReplace) {
        window.history.replaceState(null, '', url.toString());
      } else {
        window.history.pushState(null, '', url.toString());
      }
      consumeId = null;
      emitEvent();
    }, 10); // 적절하게 비동기로 미뤄서 현재 컨텍스트의 요청은 한번에 처리되게끔
  }

  return {
    syncData,
    appendData,
    subscribe,
    getSnapshot,
  };
})();

export interface UrlQueryStoreUpdateFunction<T> {
  (setter: ((value: T) => T) | Partial<T>, isReplace?: boolean): void;
}

function useUrlQueryStore<T extends AvailableType<T>>(key: string, defaultValue: T): [T, UrlQueryStoreUpdateFunction<T>] {
  const defaultRef = useRef(defaultValue);
  const value = useSyncExternalStore(urlStore.subscribe, () => urlStore.getSnapshot(key, defaultValue)) as T;

  const update = useCallback(
    (setter: ((value: T) => T) | Partial<T>, isReplace = false) => {
      urlStore.appendData(key, setter as ((value: AvailableObjectType) => AvailableObjectType) | AvailableObjectType, isReplace);
    },
    [key]
  );

  useEffect(() => {
    function syncData() {
      // 현재 popstate context 의 가장 마지막에 실행되게끔 의도
      setTimeout(() => {
        urlStore.syncData(key, defaultRef.current);
      }, 10);
    }

    window.addEventListener('popstate', syncData);
    return () => {
      window.removeEventListener('popstate', syncData);
    };
  }, [key]);

  return [value, update];
}

function urlSafesSerialize(jsonObject: AvailableObjectType): string {
  return Object.entries(jsonObject)
    .filter(
      (entities): entities is [string, string | number | boolean | null | Array<string | number | boolean | null>] =>
        entities[1] !== undefined
    )
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}.${value.map((item) => stringifyValue(item)).join('')}`;
      }

      return `${key}${stringifyValue(value)}`;
    })
    .join('|');
}

function urlSafesDeserialize<T>(text: string): Partial<T> {
  return text.split('|').reduce((jsonObject: AvailableObjectType, raw) => {
    const matcher = raw.match(DESERIALIZE_REGEX);
    if (matcher && matcher.length > 3) {
      jsonObject[matcher[1]] = parseValue(matcher[2], matcher[3]);
    }

    return jsonObject;
  }, {}) as T;
}

function stringifyValue(value: string | number | boolean | null): string {
  if (typeof value === 'number') {
    return `~${value.toString()}`;
  }
  if (typeof value === 'boolean') {
    return `!${value ? '1' : '0'}`;
  }
  if (value === null) {
    return `_`;
  }
  return `-${encodeURIComponent(value)}`;
}

function parseValue(delimiter: string, value: string): AvailableValueOrArrayOfValueType {
  if (delimiter === '.') {
    const matches = value.match(DESERIALIZE_ARRAY_REGEX);
    if (!matches) {
      return [];
    }
    return matches.map((match) => parseValue(match[0], match.slice(1)) as AvailableValueType);
  }
  if (delimiter === '~') {
    return parseFloat(value);
  }
  if (delimiter === '!') {
    return value === '1';
  }
  if (delimiter === '_') {
    return null;
  }
  return decodeURIComponent(value);
}

export default useUrlQueryStore;
